import {
  Fragment,
  memo,
  useMemo,
  useState,
  type Dispatch,
  type ReactElement,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { LessonCompletedCard, ScenarioBonusCard } from './LessonCompletedCard.js';
import { displayOrder, type VoiceMessage } from '@orborix/voice-client';
import { isGameSeed, type CorrectionLevel } from '@orborix/shared-types';
import { Waveform } from '../../components/doodles/Doodles.js';
import { MicIssueCard } from './MicIssueCard.js';
import { useAudioStuck } from '../../lib/audioStuck.js';
import { PrIconTarget } from '../program/programIcons.js';
import type { ProgramIntro } from './programIntro.js';
import { CoachCard, UserBubble } from './messages/index.js';
import { BotSourcesChip } from './messages/SourcesSheet.js';
import { buildErrors, filterErrorsByLevel, type ConvError } from './errorModel.js';
import { selectSkillChips, type SkillChipModel } from './skillChips.js';
import { selectThreadSkillChips } from './threadSkillChips.js';
import { selectCleanSentenceTicks } from './cleanSentenceTicks.js';
import { useLiveTranscriptDraft } from './hooks/useLiveTranscriptDraft.js';
import type {
  GameAttemptControl,
  GameAttemptSolution,
  GameDontKnowControl,
} from './hooks/useGameArena.js';
import type { GameBubbleStamp } from './gameArena.js';
import type { GameChallengeTag } from './gameChallenge.js';
import { GameAttemptActionButton } from './messages/GameStamp.js';
import {
  GameAttemptSolutionPanel,
  GameChallengeHead,
  GameRevealCard,
  GameSolutionButton,
} from './messages/GameRound2.js';
import { GameSummaryCard } from './GameSummaryCard.js';
import type { ConvSettings } from './conversationTypes.js';
import type { UseConversationController } from './useConversationController.js';

interface ConversationMessagesProps {
  ctrl: UseConversationController;
}

// Getters estables para las burbujas cerradas (no-streaming): siempre revelan
// todo el texto y no tienen karaoke DESDE EL WS. Definidos a nivel de módulo
// para que su identidad no cambie entre renders — pasarlos inline
// (`() => Infinity`) rompía la memoización de `ClosedMessage` aguas abajo. El
// karaoke del stream vivo sólo lo usa la burbuja VIVA (ver
// `getKaraokeRenderEndIdx`/`getKaraokeCharIndex` más abajo); el de una burbuja
// cerrada que se vuelve a escuchar lo arma `CoachCard` con `m.alignment`.
const FULL_REVEAL = (): number => Infinity;
const NO_CHAR = (): number => -1;

interface ClosedMessageProps {
  m: VoiceMessage;
  /** Posición VISIBLE en el hilo (no el índice del array): gobierna el brush. */
  index: number;
  /**
   * Entrada con slide-up. Es `isLast` EXCEPTO cuando la burbuja reemplaza a
   * una viva (commit del coach al fin del audio / del user al fin del STT):
   * ahí el contenido ya estaba en pantalla y replayar la animación se ve
   * como un parpadeo (ver `swap.noAnimIdx` en el padre).
   */
  animate: boolean;
  /** Burbuja del coach que sigue a otra del coach (header suprimible en compacta). */
  consecutive: boolean;
  /** Esta respuesta del coach SONÓ (per-burbuja, independiente del estado
   *  actual del mute): gobierna el botón de escuchar de la burbuja cerrada. */
  hadAudio: boolean;
  translationOn: boolean;
  correctionOn: boolean;
  phoneticOn: boolean;
  correctionLevelEff: CorrectionLevel;
  explainLang: ConvSettings['explainLang'];
  sessionId: string | null | undefined;
  replayTurnIndex: number | undefined;
  /**
   * Plan 42 §9.1 — con seed de juego activo el skeleton "analizando…" no se
   * muestra (el backend tampoco emite chips durante la partida; el flag se
   * limpia igual con `corrections.done`).
   */
  suppressCorrectionPending: boolean;
  /** The bubble's positive skill chip (W6, `selectSkillChips`), if any. */
  skillChip: SkillChipModel | undefined;
  /** Charla asistida: notita de lo dicho con ayuda, y si queda tapada. */
  assistedNote: string | null;
  covered: boolean;
  /** Charla asistida: la última línea del coach, grande (ver `CoachCard.featured`). */
  featured: boolean;
  /** The bubble's "no important errors" ✓ (D10, `selectCleanSentenceTicks`). */
  cleanTick: boolean;
  /** Index of the bubble in `messages` (the game controls address it by it). */
  msgIdx: number;
  /** The translate-it stamp of this attempt (plan 47), if it is one. */
  gameStamp: GameBubbleStamp | undefined;
  /** The retry / skip control, only on the newest attempt of the running game. */
  gameControl: GameAttemptControl | undefined;
  /** Its corrections are concealed while its retry is pending. */
  gameConcealed: boolean;
  onGameAction: (index: number) => void;
  onGameReveal: (index: number) => void;
  /** Coach bubble: the translate-it challenge it served (Round 2), drawn as a block. */
  gameChallenge: GameChallengeTag | undefined;
  /** Coach bubble: «No sé», only on the challenge on the table. */
  gameDontKnow: GameDontKnowControl | undefined;
  onGameDontKnow: () => void;
  /** Learner bubble: its «Ver solución» (Round 2). */
  gameSolution: GameAttemptSolution | undefined;
  onGameAskSolution: (index: number) => void;
  setDrillErr: Dispatch<SetStateAction<ConvError | null>>;
  setSettings: Dispatch<SetStateAction<ConvSettings>>;
}

/**
 * Una burbuja YA committeada (coach o usuario). Memoizada: como el coach
 * streamea token a token y eso re-renderiza `ConversationMessages` en cada
 * token, sin `memo` cada token repintaba las N burbujas anteriores → costo
 * O(n) por token, que es lo que colgaba la charla larga. Con props
 * primitivas/estables (`m` con identidad fija una vez cerrada, callbacks de
 * `useState`/`useCallback`), el shallow-compare de `memo` evita ese repintado:
 * el streaming sólo toca la burbuja VIVA, no las cerradas. El cómputo de
 * errores del usuario vive acá adentro para que sólo corra cuando ESTA burbuja
 * se re-renderiza, no en cada token global.
 */
const ClosedMessage = memo(function ClosedMessage({
  m,
  index,
  animate,
  consecutive,
  hadAudio,
  translationOn,
  correctionOn,
  phoneticOn,
  correctionLevelEff,
  explainLang,
  sessionId,
  replayTurnIndex,
  suppressCorrectionPending,
  skillChip,
  cleanTick,
  assistedNote,
  covered,
  featured,
  msgIdx,
  gameStamp,
  gameControl,
  gameConcealed,
  onGameAction,
  onGameReveal,
  gameChallenge,
  gameDontKnow,
  onGameDontKnow,
  gameSolution,
  onGameAskSolution,
  setDrillErr,
  setSettings,
}: ClosedMessageProps): ReactElement {
  if (m.who === 'bot') {
    return (
      <CoachCard
        text={m.text}
        streaming={false}
        featured={featured}
        showBrush={index === 0 ? 'short' : 'long'}
        audioOff={!hadAudio}
        translation={translationOn ? (m.translation?.text ?? null) : null}
        translationEnabled={translationOn}
        animate={animate}
        consecutive={consecutive}
        getRenderEndIdx={FULL_REVEAL}
        getCharIndex={NO_CHAR}
        {...(m.alignment ? { alignment: m.alignment } : {})}
        {...(sessionId ? { sessionId } : {})}
        {...(replayTurnIndex != null ? { replayTurnIndex } : {})}
        {...(gameChallenge
          ? {
              gameChallenge: {
                tag: gameChallenge,
                head: (
                  <GameChallengeHead
                    tag={gameChallenge}
                    dontKnow={gameDontKnow}
                    onDontKnow={onGameDontKnow}
                  />
                ),
              },
            }
          : {})}
      />
    );
  }
  const errors = correctionOn
    ? filterErrorsByLevel(
        buildErrors(m).filter((e) => phoneticOn || e.kind !== 'phonetic'),
        correctionLevelEff,
      )
    : [];
  return (
    <UserBubble
      text={m.text}
      errors={errors}
      correctionPending={Boolean(m.correctionPending) && !suppressCorrectionPending}
      streaming={false}
      animate={animate}
      onPracticeDrill={setDrillErr}
      explainLang={explainLang}
      onSetExplainLang={(lang) => setSettings((s) => ({ ...s, explainLang: lang }))}
      skillChip={skillChip}
      cleanTick={cleanTick}
      assistedNote={assistedNote}
      covered={covered}
      gameStamp={gameStamp}
      gameStampLive={!m.historical}
      onRevealConcealed={gameConcealed ? () => onGameReveal(msgIdx) : undefined}
      footer={
        gameControl || gameSolution ? (
          <GameAttemptFooter
            control={gameControl}
            solution={gameSolution}
            concealed={gameConcealed}
            onRun={() => onGameAction(msgIdx)}
            onAsk={() => onGameAskSolution(msgIdx)}
            onReveal={() => onGameReveal(msgIdx)}
          />
        ) : undefined
      }
      {...(sessionId ? { sessionId } : {})}
      {...(replayTurnIndex != null ? { replayTurnIndex } : {})}
    />
  );
});

/**
 * What goes under a translate-it attempt (plan 47): its «Ver solución» answer
 * (Round 2) and one quiet row of controls — «Ver solución» (any live «Casi» /
 * «No era eso») next to «↺ Repetirla» / «Saltear →» (newest attempt only).
 * While a rematch of the attempt is pending its solution is blurred like its
 * chips («tocá para ver»), and «Ver solución» waits: the answer must not be
 * read off the screen during the retry.
 */
function GameAttemptFooter({
  control,
  solution,
  concealed,
  onRun,
  onAsk,
  onReveal,
}: {
  control: GameAttemptControl | undefined;
  solution: GameAttemptSolution | undefined;
  concealed: boolean;
  onRun: () => void;
  onAsk: () => void;
  onReveal: () => void;
}): ReactElement {
  const showAsk = solution !== undefined && solution.canAsk && !concealed;
  return (
    <>
      {solution?.view && (
        <GameAttemptSolutionPanel
          view={solution.view}
          concealed={concealed}
          onRevealConcealed={onReveal}
          {...(solution.disabled ? {} : { onRetry: onAsk })}
        />
      )}
      {(showAsk || control) && (
        <div className="game-attempt-row">
          {showAsk && <GameSolutionButton onAsk={onAsk} disabled={solution.disabled} />}
          {control && <GameAttemptActionButton control={control} onRun={onRun} />}
        </div>
      )}
    </>
  );
}

/**
 * Lista de mensajes de la conversación (coach + usuario + burbujas vivas +
 * "pensando" + error de mic). Idéntica en móvil y escritorio: cada shell la
 * envuelve en su propio contenedor scrollable (`.app-scroll` vs `.dt-chat`).
 *
 * Reusa los leaf reales (`CoachCard`, `UserBubble`) y el modelo de errores;
 * toda la data viene del controller.
 */
export function ConversationMessages({ ctrl }: ConversationMessagesProps) {
  const audioStuck = useAudioStuck();
  const {
    session,
    messages,
    settings,
    setSettings,
    activeSeed,
    correctionLevelEff,
    isVoiceOutput,
    botAudioFlags,
    sessionId,
    historySkillCredits,
    audioRecording,
    transcript,
    assistantText,
    showLiveCoach,
    showLiveUser,
    commitPending,
    showThinking,
    isSearchingWeb,
    emptyThreadHint,
    programIntro,
    micIssue,
    micRetryPhase,
    requestMic,
    serverEnded,
    startError,
    setDrillErr,
    getKaraokeRenderEndIdx,
    getKaraokeCharIndex,
    interrupt,
    a0Mission,
    gameArena,
  } = ctrl;
  // Fuentes del turno EN CURSO: el cliente de voz las expone apenas el texto del
  // coach queda completo (state→listening), sin esperar el fin del audio. Así el
  // chip aparece bajo la burbuja viva ni bien se termina de escribir; al
  // commitear pasan a `VoiceMessage.sources` de la burbuja cerrada.
  const liveWebSources = session.liveWebSources;
  // Plan 42 §9.1 — en partida el skeleton "analizando…" se suprime (el backend
  // tampoco emite chips durante el juego; la señal se persiste igual server-side).
  const inGame = isGameSeed(activeSeed);
  // Partial vivo: prefijo estable sólido, cola en revisión atenuada. Con la
  // burbuja oculta pasamos '' para resetear (la próxima utterance arranca
  // toda como borrador).
  const liveDraftFromIdx = useLiveTranscriptDraft(showLiveUser ? transcript : '');
  // Orden REAL del hilo. El array de `messages` se llena por orden de COMMIT y
  // la burbuja del coach recién se commitea al fin de su audio: escribirle
  // antes de que termine de hablar dejaba el mensaje del user ARRIBA del coach
  // (que ya estaba en pantalla, entero, sólo terminando de sonar). `order`
  // baja esos mensajes a su lugar y `liveCoachAt` marca dónde va la burbuja
  // viva mientras la carrera sigue abierta. Las keys siguen siendo el índice
  // del array — estable — así que React REORDENA nodos en vez de remontarlos.
  const { order, liveCoachAt } = useMemo(() => displayOrder(messages), [messages]);
  // Free-talk skill credits are silent; the few that earn a chip (W6, D7/D9)
  // are decided over the whole thread (one focus is "nailed" once per session).
  // The history seeded by "Continuar" / a reload gets its chips from its
  // session summary; the merge keeps one chip per skill / focus per session.
  const liveSkillChips = useMemo(() => selectSkillChips(messages), [messages]);
  const skillChips = useMemo(
    () => selectThreadSkillChips(messages, liveSkillChips, historySkillCredits, sessionId),
    [messages, liveSkillChips, historySkillCredits, sessionId],
  );
  // D10: the discreet "no important errors" ✓ — only where a chip is not
  // already praising the bubble, never in a game nor in an A0 chat.
  const correctionsOn = settings.correction;
  const inA0Mission = a0Mission != null;
  const cleanTicks = useMemo(
    () =>
      selectCleanSentenceTicks(messages, skillChips, {
        correctionsOn,
        inGame,
        a0Mission: inA0Mission,
      }),
    [messages, skillChips, correctionsOn, inGame, inA0Mission],
  );
  // La burbuja VIVA del coach. Se arma acá (y no inline al final del hilo)
  // porque su lugar depende de `liveCoachAt`: si el user le escribió mientras
  // hablaba, va ARRIBA de esos mensajes, no al final.
  const liveCoachNode = showLiveCoach ? (
    <Fragment key="live-coach">
      <CoachCard
        text={assistantText}
        streaming
        featured={ctrl.assisted.deckVisible}
        /* Mismo brush que va a tener CERRADA (short sólo si va a ser la
           primera): si difiere, el trazo salta de alto justo al commitear
           y suma al "parpadeo" del swap viva→cerrada. */
        showBrush={liveCoachAt === 0 ? 'short' : 'long'}
        audioOff={!isVoiceOutput}
        translation={null}
        translationEnabled={settings.translation}
        animate
        getRenderEndIdx={getKaraokeRenderEndIdx}
        getCharIndex={getKaraokeCharIndex}
        onInterrupt={interrupt}
        {...(sessionId ? { sessionId } : {})}
        {...(gameArena.liveChallenge
          ? {
              gameChallenge: {
                tag: gameArena.liveChallenge,
                head: <GameChallengeHead tag={gameArena.liveChallenge} />,
              },
            }
          : {})}
      />
      {/* Fuentes del turno vivo: aparecen apenas el texto queda completo
          (state→listening), ancladas bajo la burbuja que aún está hablando —
          sin esperar el fin del audio. Al commitear, el chip de la burbuja
          cerrada (m.sources) toma la posta. */}
      {liveWebSources.length > 0 && <BotSourcesChip sources={liveWebSources} />}
    </Fragment>
  ) : null;
  // Swap viva→cerrada SIN replay del slide-up: cuando la burbuja cerrada que
  // entra REEMPLAZA a una viva (coach al fin del audio, user al commit del
  // STT), su contenido ya estaba en pantalla — replayar la animación de
  // entrada se lee como "la burbuja desaparece y reaparece". Detectamos el
  // reemplazo comparando con el render anterior (patrón derive-from-previous,
  // ajuste durante el render) y lo dejamos sticky por índice: esa burbuja no
  // anima aunque haya renders posteriores.
  const userLiveNow = showLiveUser && transcript.trim() !== '';
  const [swap, setSwap] = useState({
    len: messages.length,
    coachLive: showLiveCoach,
    userLive: userLiveNow,
    noAnimIdx: -1,
  });
  if (
    swap.len !== messages.length ||
    swap.coachLive !== showLiveCoach ||
    swap.userLive !== userLiveNow
  ) {
    const last = messages[messages.length - 1];
    const replacesLive =
      messages.length > swap.len &&
      last !== undefined &&
      ((last.who === 'bot' && swap.coachLive) || (last.who === 'user' && swap.userLive));
    setSwap({
      len: messages.length,
      coachLive: showLiveCoach,
      userLive: userLiveNow,
      noAnimIdx: replacesLive ? messages.length - 1 : swap.noAnimIdx,
    });
  }

  return (
    <>
      {startError && (
        <div
          className="card"
          style={{
            borderColor: 'rgba(248,113,113,0.3)',
            background: 'rgba(248,113,113,0.06)',
            color: '#fca5a5',
          }}
        >
          {startError}
        </div>
      )}

      {/* Sin mic usable la charla NO se cae: abre en sólo-texto (el lifecycle
          arranca la sesión con `inputMode:'text'`). Por eso el aviso es
          informativo y no una card de error — dice qué canal quedó vivo y
          OFRECE el permiso (recupera la voz en esta misma charla), mientras el
          coach ya está saludando abajo. Con la sesión cerrada por el server se
          va: ahí ni el texto ni el permiso tienen a dónde ir (el composer está
          inhabilitado) y la card terminal es la única acción honesta. */}
      {micIssue && serverEnded == null && (
        <MicIssueCard
          issue={micIssue}
          phase={micRetryPhase}
          onRequest={requestMic}
          audioStuck={audioStuck}
        />
      )}

      {programIntro && <ProgramIntroLine intro={programIntro} />}

      {/* Hilo vacío y mudo (huecos de "Continuar"/reattach): feedback en vez
          de pantalla negra. 'loading' = sembrando el transcript previo;
          'your-turn' = reattach sin turnos, el coach no re-saluda — arranca
          el user. Mismo lenguaje visual que el "pensando". */}
      {emptyThreadHint === 'loading' && (
        <div
          className="slide-up"
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--text-muted)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 13,
          }}
        >
          <Waveform color="var(--lime)" size={20} />
          <span>
            Cargando la charla<span className="dots-anim" />
          </span>
        </div>
      )}
      {emptyThreadHint === 'your-turn' && (
        <div
          className="slide-up"
          role="status"
          style={{
            color: 'var(--text-muted)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 13,
          }}
        >
          Retomaste la charla — seguí vos: mantené el mic o escribí abajo.
        </div>
      )}

      {(() => {
        // Las burbujas históricas (sembradas al "Continuar conversación") son
        // siempre el prefijo del array; el último de ellas marca dónde va el
        // separador "continuaste desde acá".
        let lastHistIdx = -1;
        messages.forEach((m, i) => {
          if (m.historical) lastHistIdx = i;
        });
        // `turnIndex` canónico de la sesión viva para reproducir el audio REAL
        // persistido (RECORD_SESSION_AUDIO). Contamos turnos por reply del bot:
        // el bot reply N = turno N (incluye el saludo coach-first), y el mensaje
        // del user pertenece al turno del bot que viene a continuación. Esto
        // matchea el `turns.turn_index` del server sin tocar el protocolo, y es
        // robusto cuando el coach habla primero. Se cuenta en orden de PANTALLA
        // (= el orden real de la charla): en el array un mensaje que le ganó de
        // mano al coach queda antes de la burbuja que lo precedió al hablar, y
        // contarlo ahí le daba el turno del coach en vez del suyo.
        let botTurns = 0;
        // Emisor de la última burbuja RENDEREADA (saltea las 'tool'): marca
        // las burbujas del coach consecutivas para suprimir su header en
        // densidad compacta.
        let prevWho: VoiceMessage['who'] | null = null;
        // Última burbuja del HILO (no del array): la única que anima al entrar.
        const lastDisplayIdx = order[order.length - 1] ?? -1;
        // Charla asistida: la última línea del coach va grande (la viva, si está hablando).
        let featuredIdx = -1;
        if (ctrl.assisted.deckVisible && liveCoachNode === null) {
          for (const i of order) if (messages[i]?.who === 'bot') featuredIdx = i;
        }
        const out: ReactNode[] = [];
        order.forEach((i, pos) => {
          // La burbuja viva del coach va en su lugar de la charla: si el user
          // le escribió encima, arriba de esos mensajes — que es exactamente
          // donde va a caer su commit.
          if (pos === liveCoachAt) out.push(liveCoachNode);
          const m = messages[i];
          if (m === undefined || m.who === 'tool') return;
          const consecutive = m.who === 'bot' && prevWho === 'bot';
          prevWho = m.who;
          let replayTurnIndex: number | undefined;
          if (!m.historical) {
            // El contador SIEMPRE avanza (mantiene el índice canónico), pero
            // sólo exponemos replayTurnIndex si la sesión está grabando: sin
            // grabación, el coach cae a speakText y el user no muestra play
            // (su audio no se puede re-sintetizar). Evita el 404 muerto.
            // Un turno TECLEADO (`typed`) tampoco tiene voz que grabar, aunque
            // la sesión grabe: sin este gate el user veía un play que siempre
            // fallaba con "el backend no tiene el audio de este turno".
            if (m.who === 'bot') {
              botTurns += 1;
              if (audioRecording) replayTurnIndex = botTurns;
            } else if (audioRecording && !m.typed) {
              replayTurnIndex = botTurns + 1;
            }
          }
          const node = (
            <ClosedMessage
              m={m}
              index={pos}
              animate={i === lastDisplayIdx && i !== swap.noAnimIdx}
              consecutive={consecutive}
              hadAudio={botAudioFlags[i] ?? true}
              translationOn={settings.translation}
              correctionOn={settings.correction}
              phoneticOn={settings.phonetic}
              correctionLevelEff={correctionLevelEff}
              explainLang={settings.explainLang}
              sessionId={sessionId}
              replayTurnIndex={replayTurnIndex}
              suppressCorrectionPending={inGame}
              skillChip={skillChips.get(i)}
              cleanTick={cleanTicks.has(i)}
              assistedNote={m.who === 'user' ? ctrl.assisted.noteFor(m.text, i) : null}
              covered={m.who === 'user' && ctrl.assisted.isCovered(m.text, i)}
              featured={i === featuredIdx}
              msgIdx={i}
              gameStamp={gameArena.stamps.get(i)}
              gameControl={gameArena.attemptControl?.index === i ? gameArena.attemptControl : undefined}
              gameConcealed={gameArena.concealed.has(i)}
              onGameAction={gameArena.runAttemptAction}
              onGameReveal={gameArena.revealAttempt}
              gameChallenge={gameArena.challenges.get(i)}
              gameDontKnow={gameArena.dontKnow?.index === i ? gameArena.dontKnow : undefined}
              onGameDontKnow={gameArena.onDontKnow}
              gameSolution={gameArena.solutions.get(i)}
              onGameAskSolution={gameArena.askSolution}
              setDrillErr={setDrillErr}
              setSettings={setSettings}
            />
          );
          out.push(
            <Fragment key={i}>
              {m.historical ? (
                <div className="conv-historical-msg" style={{ opacity: 0.5 }}>
                  {node}
                </div>
              ) : (
                node
              )}
              {/* Fuentes de la búsqueda web ANCLADAS bajo la burbuja que la hizo:
                  scrollean con su mensaje; cada búsqueda tiene su propio chip. */}
              {m.who === 'bot' && m.sources && m.sources.length > 0 && (
                <BotSourcesChip sources={m.sources} />
              )}
              {i === lastHistIdx ? <ResumeDivider /> : null}
            </Fragment>,
          );
          // «No sé» cards anchored after this message (plan 47 Round 2).
          for (const card of gameArena.revealCards.get(i) ?? []) {
            out.push(<GameRevealCard key={card.key} card={card} />);
          }
          // A translate-it game that ended after this message: its summary
          // card (plan 47 Phase 4).
          const summary = gameArena.summaries.get(i);
          if (summary)
            out.push(
              <GameSummaryCard
                key={`game-summary-${i}`}
                summary={summary}
                practice={{ sessionId, host: ctrl.gamePractice }}
              />,
            );
        });
        // Nadie le ganó de mano: la viva cierra el hilo (el caso de siempre).
        if (liveCoachAt === order.length) out.push(liveCoachNode);
        return out;
      })()}

      {/* "Navegando la web…" — tiene prioridad sobre "pensando": cuando el
          coach dispara una búsqueda web nativa, la latencia extra (1-3s) se
          explica con este indicador en vez de quedar mudo. */}
      {isSearchingWeb && !showLiveCoach && (
        <div
          className="slide-up"
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--text-muted)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 13,
          }}
        >
          <span aria-hidden style={{ fontSize: 16 }}>
            🌐
          </span>
          <span>
            Navegando la web<span className="dots-anim" />
          </span>
        </div>
      )}

      {showThinking && !isSearchingWeb && (
        <div
          className="slide-up"
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--text-muted)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 13,
          }}
        >
          <Waveform color="var(--lime)" size={20} />
          <span>
            Coach está pensando<span className="dots-anim" />
          </span>
        </div>
      )}

      {/* La burbuja viva del user aparece recién con transcript REAL: la barra
          de mic ya dice "Te escucho" — una burbuja vacía duplicando ese estado
          era ruido (dos señales para el mismo hecho). Excepción: con un commit
          en vuelo (tap de enviar) la burbuja se muestra aunque no haya partial
          pintado — es el feedback "Enviando…" del tap. */}
      {showLiveUser && (transcript.trim() !== '' || commitPending) && (
        <UserBubble
          text={transcript}
          errors={[]}
          correctionPending={false}
          streaming
          sending={commitPending}
          animate
          draftFromIdx={liveDraftFromIdx}
          onPracticeDrill={setDrillErr}
        />
      )}

      {ctrl.lessonCompletion && (
        <LessonCompletedCard
          completion={ctrl.lessonCompletion}
          connected={ctrl.connectionStatus === 'open' || (ctrl.lessonCompletion.phase === 'closed' && ctrl.connectionStatus !== 'lost')}
          onFinish={ctrl.leaveToProgram}
          onProgram={ctrl.leaveToProgram}
          onChoose={ctrl.chooseLesson}
          scenarioActions={ctrl.scenarioEndActions}
        />
      )}
      {ctrl.bonusPending && (
        <ScenarioBonusCard bonusLabel={ctrl.bonusPending} actions={ctrl.scenarioEndActions} />
      )}
      <div style={{ height: 6 }} />
    </>
  );
}

/**
 * Qué se practica en esta charla guiada, arriba del hilo (ver `programIntro.ts`).
 * Informativa y fija: no se descarta, queda como encabezado de la charla.
 */
function ProgramIntroLine({ intro }: { intro: ProgramIntro }): ReactElement {
  return (
    <div className="program-intro slide-up" role="note">
      <span className="program-intro-ic" aria-hidden="true">
        <PrIconTarget size={13} color="var(--coral)" />
      </span>
      <div className="program-intro-body">
        <div className="program-intro-title">Práctica guiada · {intro.title}</div>
        <div className="program-intro-blurb">{intro.blurb}</div>
        <div className="program-intro-hint">
          Respondé natural, como en una charla; arriba ves tus {intro.objectiveCount} objetivos.
        </div>
      </div>
    </div>
  );
}

/**
 * Separador entre las burbujas históricas (sesión retomada) y el turno vivo.
 * Marca visualmente "desde acá sigue la charla nueva".
 */
function ResumeDivider(): ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '6px 2px',
        color: 'var(--text-muted)',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 11,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}
    >
      <span style={{ flex: 1, height: 1, background: 'var(--border, rgba(255,255,255,0.12))' }} />
      <span>continuaste desde acá</span>
      <span style={{ flex: 1, height: 1, background: 'var(--border, rgba(255,255,255,0.12))' }} />
    </div>
  );
}
